import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment, Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// Brand colors
const BRAND_BLUE = '#2563EB';
const BRAND_DARK = '#1E40AF';
const BRAND_LIGHT = '#D0E2FF';

// ----------------------------------------------------------------------

function BearEmoji({ isPasswordFocus, usernameLength }) {
  // Eye X offset: tracks typing progress in username field (-8 → +8)
  const progress = Math.min(usernameLength / 18, 1);
  const ex = -8 + progress * 16;

  // Hand position animation: Move up to cover eyes when password field is focused
  const handTranslationY = isPasswordFocus ? -55 : 0;

  return (
    <Box sx={{ 
        textAlign: 'center', 
        mb: 3, // Increased margin bottom
        userSelect: 'none', 
        position: 'relative', 
        height: { xs: 120, sm: 140 }, // Responsive height
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center' 
    }}>
      <svg 
        width="100%" 
        height="100%" 
        viewBox="0 0 120 120" 
        xmlns="http://www.w3.org/2000/svg" 
        style={{ overflow: 'visible', maxWidth: 120 }}
      >
        {/* Ears */}
        <circle cx="22" cy="28" r="16" fill={BRAND_DARK} />
        <circle cx="98" cy="28" r="16" fill={BRAND_DARK} />
        <circle cx="22" cy="28" r="9" fill={BRAND_LIGHT} />
        <circle cx="98" cy="28" r="9" fill={BRAND_LIGHT} />

        {/* Head */}
        <circle cx="60" cy="65" r="50" fill={BRAND_BLUE} />

        {/* Shine on head */}
        <ellipse cx="44" cy="38" rx="10" ry="6" fill="rgba(255,255,255,0.18)" />

        {/* Muzzle */}
        <ellipse cx="60" cy="83" rx="19" ry="13" fill={BRAND_LIGHT} />

        {/* Nose */}
        <ellipse cx="60" cy="76" rx="6" ry="4" fill={BRAND_DARK} />

        {/* Mouth smile */}
        <path d="M 51 85 Q 60 93 69 85" stroke={BRAND_DARK} strokeWidth="2" fill="none" strokeLinecap="round" />

        {/* EYES */}
        <g>
            {!isPasswordFocus ? (
            <>
                {/* Left eye — open, tracking */}
                <g transform={`translate(${ex}, 0)`} style={{ transition: 'transform 0.1s ease-out' }}>
                    <circle cx="42" cy="58" r="10" fill="white" />
                    <circle cx="42" cy="58" r="5" fill="#111" />
                    <circle cx="44" cy="56" r="1.8" fill="white" />
                </g>
                {/* Right eye — open, tracking */}
                <g transform={`translate(${ex}, 0)`} style={{ transition: 'transform 0.1s ease-out' }}>
                    <circle cx="78" cy="58" r="10" fill="white" />
                    <circle cx="78" cy="58" r="5" fill="#111" />
                    <circle cx="80" cy="56" r="1.8" fill="white" />
                </g>
            </>
            ) : (
            <>
                {/* Left eye — closed lid */}
                <circle cx="42" cy="58" r="10" fill="white" />
                <path d="M 34 58 Q 42 50 50 58" fill="none" stroke={BRAND_DARK} strokeWidth="2" strokeLinecap="round" />
                {/* Right eye — closed lid */}
                <circle cx="78" cy="58" r="10" fill="white" />
                <path d="M 70 58 Q 78 50 86 58" fill="none" stroke={BRAND_DARK} strokeWidth="2" strokeLinecap="round" />
            </>
            )}
        </g>

        {/* HANDS - Animation */}
        <g style={{ transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', transform: `translateY(${handTranslationY}px)` }}>
            {/* Left Hand */}
            <circle cx="28" cy="120" r="24" fill={BRAND_DARK} />
            <circle cx="28" cy="120" r="14" fill={BRAND_LIGHT} opacity="0.2" />
            {/* Right Hand */}
            <circle cx="92" cy="120" r="24" fill={BRAND_DARK} />
            <circle cx="92" cy="120" r="14" fill={BRAND_LIGHT} opacity="0.2" />
        </g>
      </svg>

      <Typography
        variant="caption"
        sx={{ 
            color: 'text.secondary', 
            fontStyle: 'italic', 
            display: 'block', 
            mt: 2.5, // Added margin top for clear separation
            fontWeight: 500,
            fontSize: { xs: '0.7rem', sm: '0.75rem' } 
        }}
      >
        {isPasswordFocus
          ? "I'm not looking! 🙈"
          : usernameLength > 0
          ? 'Watching you type... 👀'
          : 'Welcome back! 👋'}
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocus, setIsPasswordFocus] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [usernameLength, setUsernameLength] = useState(0);

  const savedUsername = localStorage.getItem('remembered_username') || '';
  const savedPassword = localStorage.getItem('remembered_password') || '';

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Username or Email or Phone Number is required'),
    password: Yup.string().required('Password is required'),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: {
      email: savedUsername,
      password: savedPassword,
    },
  });

  const { setError, handleSubmit, watch, formState: { errors, isSubmitting } } = methods;

  const emailValue = watch('email', savedUsername);

  useEffect(() => {
    setUsernameLength((emailValue || '').length);
  }, [emailValue]);

  useEffect(() => {
    if (savedUsername && savedPassword) setRememberMe(true);
  }, []); // eslint-disable-line

  const onSubmit = async (data) => {
    try {
      if (rememberMe) {
        localStorage.setItem('remembered_username', data.email);
        localStorage.setItem('remembered_password', data.password);
      } else {
        localStorage.removeItem('remembered_username');
        localStorage.removeItem('remembered_password');
      }
      await login(data.email, data.password);
    } catch (error) {
      if (isMountedRef.current) {
        setError('afterSubmit', error);
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {/* Animated Bear */}
      <BearEmoji isPasswordFocus={isPasswordFocus} usernameLength={usernameLength} />

      <Stack spacing={3} sx={{ mt: 3 }}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Username, Phone or Email
          </Typography>
          <RHFTextField
            name="email"
            placeholder="Username, Phone or Email"
            onFocus={() => setIsPasswordFocus(false)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#F9FAFB' } }}
          />
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Password</Typography>
            <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}
              sx={{ color: 'primary.main', textDecoration: 'none' }}>
              Forgot Password?
            </Link>
          </Stack>
          <RHFTextField
            name="password"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            onFocus={() => setIsPasswordFocus(true)}
            onBlur={() => setIsPasswordFocus(false)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#F9FAFB' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <Iconify
                      icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                      sx={{ color: 'text.disabled' }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Remember me on this device
            </Typography>
          }
          sx={{ mt: -0.5 }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          mt: 3,
          py: 2,
          bgcolor: 'primary.main',
          color: '#fff',
          fontSize: '1rem',
          fontWeight: 700,
          borderRadius: 2,
          '&:hover': { bgcolor: 'primary.dark' },
          boxShadow: 'none',
        }}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}
